import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  Paper,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  BugReport as BugReportIcon,
  Terminal as TerminalIcon,
  Map as MapIcon,
  FileDownload as FileDownloadIcon,
  List as ListIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';

const CloudflareLab = () => {
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState(null);
  const [search, setSearch] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [recentZones, setRecentZones] = useState([]);
  const [debugUrl, setDebugUrl] = useState('');
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [sitemapSubpath, setSitemapSubpath] = useState('');
  const [sitemapLoading, setSitemapLoading] = useState(false);
  const [sitemapResult, setSitemapResult] = useState(null);
  const [showSitemapUrls, setShowSitemapUrls] = useState(false);
  const [purgeType, setPurgeType] = useState('url');
  const { user, isAdmin, isManager, isManagerOrAdmin } = useAuth();

  const buildFullUrl = (raw, zoneName) => {
    if (!raw) return '';
    const input = String(raw).trim();
    if (/^https?:\/\//i.test(input)) return input;
    if (input.includes('.')) {
      try {
        const u = new URL(`https://${input}`);
        if (!u.pathname || u.pathname === '') u.pathname = '/';
        return u.toString();
      } catch {
        return `https://${input}`;
      }
    }
    const path = input.startsWith('/') ? input : `/${input}`;
    const host = zoneName || '';
    return `https://${host}${path}`;
  };

  useEffect(() => {
    const loadZones = async () => {
      try {
        setZonesLoading(true);
        const resp = await api.cloudflare.listZones({ search, page: 1, per_page: 25 });
        setZones(resp?.zones || []);
      } catch {
        toast.error('Failed to load Cloudflare zones');
      } finally {
        setZonesLoading(false);
      }
    };
    const t = setTimeout(loadZones, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    try {
      const key = `cfRecentZones_${user?.id || 'anon'}`;
      const saved = localStorage.getItem(key);
      if (saved) setRecentZones(JSON.parse(saved) || []);
    } catch {}
  }, [user?.id]);

  useEffect(() => {
    if (selectedZone?.id) {
      try {
        const key = `cfRecentZones_${user?.id || 'anon'}`;
        const existing = Array.isArray(recentZones) ? [...recentZones] : [];
        const dedup = [{ id: selectedZone.id, name: selectedZone.name }, ...existing.filter(z => z.id !== selectedZone.id)].slice(0, 5);
        setRecentZones(dedup);
        localStorage.setItem(key, JSON.stringify(dedup));
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone?.id]);

  const handleSyncZones = async () => {
    try {
      setSyncing(true);
      await api.cloudflare.syncZones();
      const resp = await api.cloudflare.listZones({ search, page: 1, per_page: 25 });
      setZones(resp?.zones || []);
      toast.success('Cloudflare zones synced');
    } catch (e) {
      toast.error(e.message || 'Failed to sync zones');
    } finally {
      setSyncing(false);
    }
  };

  const ensureZoneId = () => {
    const zoneId = selectedZone?.id || '';
    if (!zoneId || zoneId.trim().length < 5) {
      toast.error('Select a Cloudflare zone first');
      return false;
    }
    return true;
  };

  const handlePurgeUrls = async () => {
    if (!ensureZoneId()) return;
    const zoneId = selectedZone.id;
    const fullUrl = buildFullUrl(url, selectedZone?.name);
    if (!fullUrl) {
      toast.error('Enter a URL to purge');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const resp = await api.cloudflare.purgeUrl(zoneId, [fullUrl]);
      setResult(resp);
      toast.success(`URL purged on ${selectedZone.name}`);
    } catch (e) {
      setResult(e.data || { error: e.message });
      toast.error(e.message || 'Cloudflare API error');
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeAll = async () => {
    if (!ensureZoneId()) return;
    const zoneId = selectedZone.id;
    setLoading(true);
    setResult(null);
    try {
      const resp = await api.cloudflare.purgeAll(zoneId);
      setResult(resp);
      toast.success(`Purged everything on ${selectedZone.name}`);
    } catch (e) {
      setResult(e.data || { error: e.message });
      toast.error(e.message || 'Cloudflare API error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (purgeType === 'url') {
      handlePurgeUrls();
    } else if (purgeType === 'purge_all') {
      handlePurgeAll();
    }
  };

  const handleDebugHeaders = async () => {
    if (!ensureZoneId()) return;
    const full = buildFullUrl(debugUrl || '/', selectedZone?.name);
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const resp = await api.cloudflare.debugHeaders(full);
      const curlCommand = `curl -I "${full}"`;
      setDebugResult({ ...resp, curlCommand });
      toast.success('Debug headers fetched successfully');
    } catch (e) {
      toast.error(e.message || 'Failed to fetch headers');
      setDebugResult({
        error: e.message || 'Failed to fetch headers',
        url: full,
        curlCommand: `curl -I "${full}"`
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleScanSitemap = async () => {
    if (!ensureZoneId()) return;
    setSitemapLoading(true);
    setSitemapResult(null);
    try {
      const resp = await api.cloudflare.scanSitemap(selectedZone?.name, sitemapSubpath);
      setSitemapResult(resp);
      if (resp.found) {
        let msg = resp.subSitemapsCount > 0
          ? `Found ${resp.totalUrls} URLs from ${resp.subSitemapsCount + 1} sitemap(s)`
          : `Found ${resp.totalUrls} URLs in sitemap`;
        if (resp.failedCount > 0) {
          msg += ` (${resp.failedCount} failed)`;
          toast.warn(msg);
        } else {
          toast.success(msg);
        }
        setShowSitemapUrls(true);
      } else {
        toast.warn('No sitemap found at common locations');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to scan sitemap');
      setSitemapResult({
        found: false,
        error: e.message || 'Failed to scan sitemap',
        domain: selectedZone?.name
      });
    } finally {
      setSitemapLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const applySitemapUrl = (urlPath) => {
    setUrl(urlPath);
    toast.success(`URL applied: ${urlPath}`);
  };

  const visitPage = (urlPath) => {
    const domain = selectedZone?.name;
    if (!domain) return;
    const fullUrl = `https://${domain}${urlPath}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const exportToCSV = () => {
    const domain = selectedZone?.name;
    if (!domain || !sitemapResult?.urls?.length) {
      toast.error('No URLs to export');
      return;
    }
    const headers = ['URL Path', 'Full URL', 'Last Modified'];
    const rows = [headers];
    sitemapResult.urls.forEach((u) => {
      rows.push([
        u.path,
        `https://${domain}${u.path}`,
        u.lastmod || 'Not specified'
      ]);
    });
    const csvContent = rows.map(row =>
      row.map(field =>
        field.includes(',') || field.includes('"')
          ? `"${field.replace(/"/g, '""')}"`
          : field
      ).join(',')
    ).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const urlObj = URL.createObjectURL(blob);
    link.setAttribute('href', urlObj);
    link.setAttribute('download', `sitemap-urls-${domain}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(urlObj);
    toast.success(`Exported ${sitemapResult.urls.length} URLs to CSV`);
  };

  const getPurgeTypeIcon = (type) => {
    switch (type) {
      case 'url': return <LinkIcon />;
      case 'purge_all': return <DeleteIcon />;
      default: return <DeleteIcon />;
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Main Purge Form - matching Fastly layout */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CloudIcon sx={{ color: '#f38020' }} />
              <Typography variant="h6">
                Cloudflare Cache Purge
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={zones}
                    loading={zonesLoading}
                    getOptionLabel={(o) => o?.name || ''}
                    value={selectedZone}
                    onChange={(e, v) => setSelectedZone(v)}
                    onInputChange={(e, v) => setSearch(v || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Zone (Searchable)"
                        placeholder="Type to search zones..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {zonesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Box 
                            component="img" 
                            src="/cloudflare-logo-png_seeklogo-294312.png" 
                            alt="Cloudflare" 
                            sx={{ width: 20, height: 20, objectFit: 'contain' }} 
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.status || 'active'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    noOptionsText={zonesLoading ? "Loading zones..." : "No zones found"}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedZone}>
                    <InputLabel>Purge Type</InputLabel>
                    <Select
                      value={purgeType}
                      onChange={(e) => setPurgeType(e.target.value)}
                      label="Purge Type"
                    >
                      <MenuItem value="url">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinkIcon /> URL Specific
                        </Box>
                      </MenuItem>
                      <MenuItem value="purge_all">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DeleteIcon /> Purge Everything
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Purge Type Explanations */}
              {purgeType && selectedZone && (
                <Box sx={{ mt: 2 }}>
                  {purgeType === 'url' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>URL Purging:</strong> Removes cache for a specific page or file on <strong>{selectedZone.name}</strong>. 
                        Enter the path, hostname, or full URL.
                      </Typography>
                    </Alert>
                  )}

                  {purgeType === 'purge_all' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>⚠️ Purge Everything:</strong> This will remove ALL cached content for <strong>{selectedZone.name}</strong>. 
                        This may temporarily increase load on your origin server. Use with caution!
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* URL Input for URL purge type */}
              {purgeType === 'url' && selectedZone && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label="URL to Purge"
                  placeholder="e.g., /page, example.com/page, or https://example.com/page"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  helperText="Enter a path, hostname + path, or full URL. We'll construct the full URL automatically."
                />
              )}

              {selectedZone && purgeType && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ mt: 3 }}
                  disabled={loading || (purgeType === 'url' && !url)}
                  startIcon={loading ? <CircularProgress size={20} /> : getPurgeTypeIcon(purgeType)}
                  color={purgeType === 'purge_all' ? 'warning' : 'primary'}
                >
                  {loading ? 'Purging...' : 'Execute Purge'}
                </Button>
              )}
            </Box>

            {/* Selected Zone Information - matching Fastly's "Selected Site Information" */}
            {selectedZone && (
              <Paper sx={{ mt: 3, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Zone Information
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Zone:</strong> {selectedZone.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Zone ID:</strong> {selectedZone.id}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Status:</strong> {selectedZone.status || 'active'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Available Purge Types:</strong>
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  <Chip label="URL" size="small" />
                  <Chip label="Purge Everything" size="small" />
                </Box>
              </Paper>
            )}

            {/* Purge Result */}
            {result && (
              <Alert severity={result?.success ? 'success' : 'info'} sx={{ mt: 2 }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </Alert>
            )}

            {/* Debug Tools Section - matching Fastly styling */}
            {(isAdmin || isManager) && (
              <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.900', color: 'lime' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'lime', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BugReportIcon fontSize="small" />
                  Debug Tools (Cache Headers Check)
                </Typography>
                <Typography variant="caption" sx={{ color: '#888', mb: 2, display: 'block' }}>
                  💡 Tip: Check headers and scan sitemaps for the selected zone.
                </Typography>

                {!selectedZone && (
                  <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'lime', '& .MuiAlert-icon': { color: 'lime' } }}>
                    Select a zone above to use the debug tools
                  </Alert>
                )}

                {/* Sitemap Subpath + Scan button */}
                {selectedZone && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Sitemap Subpath (Optional)"
                      placeholder="e.g., uk, fr, blog"
                      value={sitemapSubpath}
                      onChange={(e) => setSitemapSubpath(e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              {selectedZone?.name}/
                            </Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              /sitemap.xml
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          '& fieldset': { borderColor: '#444' },
                          '&:hover fieldset': { borderColor: '#666' },
                          '&.Mui-focused fieldset': { borderColor: 'cyan' },
                        },
                        '& .MuiInputLabel-root': { color: '#888' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'cyan' },
                        '& .MuiInputBase-input': { color: 'lime' },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleScanSitemap}
                      disabled={sitemapLoading || !selectedZone}
                      startIcon={sitemapLoading ? <CircularProgress size={16} /> : <MapIcon />}
                      sx={{
                        whiteSpace: 'nowrap',
                        borderColor: 'cyan',
                        color: 'cyan',
                        '&:hover': { borderColor: 'lightcyan', color: 'lightcyan' },
                        '&:disabled': { borderColor: '#666', color: '#666' }
                      }}
                    >
                      Scan Sitemap
                    </Button>
                  </Box>
                )}

                {/* URL Path + Show Headers */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="URL Path"
                    placeholder="/path/to/page"
                    value={debugUrl}
                    onChange={(e) => setDebugUrl(e.target.value)}
                    InputProps={{
                      startAdornment: selectedZone ? (
                        <InputAdornment position="start">
                          <Typography variant="body2" sx={{ color: '#888' }}>
                            {selectedZone?.name}
                          </Typography>
                        </InputAdornment>
                      ) : undefined
                    }}
                    sx={{
                      minWidth: 280,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#666' },
                        '&.Mui-focused fieldset': { borderColor: 'cyan' },
                      },
                      '& .MuiInputLabel-root': { color: '#888' },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'cyan' },
                      '& .MuiInputBase-input': { color: 'lime' },
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDebugHeaders}
                    disabled={debugLoading || !debugUrl || !selectedZone}
                    startIcon={debugLoading ? <CircularProgress size={16} /> : <TerminalIcon />}
                    sx={{
                      borderColor: 'lime',
                      color: 'lime',
                      '&:hover': { borderColor: 'lightgreen', color: 'lightgreen' },
                      '&:disabled': { borderColor: '#666', color: '#666' }
                    }}
                  >
                    {!selectedZone ? 'Select Zone' : !debugUrl ? 'Enter Path' : 'Show Headers'}
                  </Button>
                </Box>

                {/* Headers Result Panel */}
                {debugResult && (
                  <Paper sx={{
                    p: 2,
                    bgcolor: '#000',
                    color: 'lime',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    border: '1px solid lime',
                    borderRadius: 1
                  }}>
                    {debugResult.error ? (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#ff6b6b', mb: 1 }}>
                          ERROR: {debugResult.error}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'lime', mb: 1 }}>
                          URL: {debugResult.url}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                          <Typography variant="body2" sx={{ color: 'lime' }}>
                            Debug Command:
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => copyToClipboard(debugResult.curlCommand)}
                            startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                            sx={{ color: 'lime', fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                          >
                            Copy
                          </Button>
                        </Box>
                        <pre style={{ margin: 0, color: 'lime', background: 'transparent' }}>
                          {debugResult.curlCommand}
                        </pre>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2" sx={{ color: 'lime' }}>
                            Cache Headers - {debugResult.url}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'orange' }}>
                            Status: {debugResult.statusCode} {debugResult.statusText}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: 'yellow', mb: 1 }}>
                            CACHE HEADERS:
                          </Typography>
                          {Object.entries(debugResult.cacheHeaders || {}).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                              <Typography sx={{ color: 'cyan', minWidth: '180px' }}>{key}:</Typography>
                              <Typography sx={{ color: value === 'Not found' ? '#666' : 'lime' }}>{value}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                          <Typography variant="body2" sx={{ color: 'lime' }}>Curl command:</Typography>
                          <Button
                            size="small"
                            onClick={() => copyToClipboard(debugResult.curlCommand)}
                            startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                            sx={{ color: 'lime', fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                          >
                            Copy
                          </Button>
                        </Box>
                        <pre style={{ margin: 0, color: 'lime', background: 'transparent' }}>{debugResult.curlCommand}</pre>
                        <Typography variant="caption" sx={{ color: '#666', mt: 2, display: 'block' }}>
                          Fetched at: {new Date(debugResult.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Sitemap Results */}
                {sitemapResult && (
                  <Paper sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: sitemapResult.found ? '#003d00' : '#2d1b1b',
                    border: sitemapResult.found ? '1px solid cyan' : '1px solid #ff6b6b',
                    borderRadius: 1
                  }}>
                    {sitemapResult.found ? (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2" sx={{ color: 'cyan' }}>
                            ✅ Sitemap Found: {sitemapResult.sitemapUrl}
                            {sitemapResult.timedOut && (
                              <Typography variant="caption" sx={{ color: 'orange', display: 'block' }}>
                                ⏳ Partial results (scanner timed out)
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'lime' }}>
                            {sitemapResult.totalUrls} URLs found
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2" sx={{ color: 'yellow' }}>PAGE URLS:</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={exportToCSV}
                              startIcon={<FileDownloadIcon sx={{ fontSize: 12 }} />}
                              sx={{ color: 'lime', fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                            >
                              Export CSV
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setShowSitemapUrls(!showSitemapUrls)}
                              startIcon={<ListIcon sx={{ fontSize: 12 }} />}
                              sx={{ color: 'cyan', fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                            >
                              {showSitemapUrls ? 'Hide' : 'Show'} URLs
                            </Button>
                          </Box>
                        </Box>
                        {showSitemapUrls && (
                          <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #444', borderRadius: 1, p: 1, bgcolor: '#000' }}>
                            {sitemapResult.urls?.map((u, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 0.5,
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  '&:hover': { bgcolor: '#1a1a1a' },
                                  cursor: 'pointer'
                                }}
                                onClick={() => applySitemapUrl(u.path)}
                              >
                                <Typography sx={{ color: 'lime', fontFamily: 'monospace', fontSize: '0.8rem', flex: 1 }}>
                                  {u.path}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); visitPage(u.path); }}
                                    startIcon={<OpenInNewIcon sx={{ fontSize: 10 }} />}
                                    sx={{ color: 'orange', fontSize: '0.6rem', minWidth: 'auto', p: 0.25 }}
                                  >
                                    Visit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); applySitemapUrl(u.path); }}
                                    startIcon={<ListIcon sx={{ fontSize: 10 }} />}
                                    sx={{ color: 'cyan', fontSize: '0.6rem', minWidth: 'auto', p: 0.25 }}
                                  >
                                    Use
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                            {sitemapResult.totalUrls > 100 && (
                              <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                                Showing first 100 of {sitemapResult.totalUrls} URLs
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#ff6b6b', mb: 1 }}>❌ No sitemap found</Typography>
                        <Typography variant="body2" sx={{ color: '#888' }}>{sitemapResult.message || sitemapResult.error}</Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Paper>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Right Sidebar - matching Fastly layout */}
      <Grid item xs={12} md={4}>
        {/* Recent Zones */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Zones
            </Typography>
            {recentZones.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {recentZones.map((z) => (
                  <Chip
                    key={z.id}
                    label={z.name}
                    onClick={() => setSelectedZone(z)}
                    variant={selectedZone?.id === z.id ? 'filled' : 'outlined'}
                    color={selectedZone?.id === z.id ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent zones. Select a zone to get started.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Sync Zones (Managers & Admins) */}
        {isManagerOrAdmin && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Zone Management
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSyncZones}
                disabled={syncing || zonesLoading}
                startIcon={syncing ? <CircularProgress size={18} /> : null}
              >
                {syncing ? 'Syncing...' : 'Sync Zones from Cloudflare'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Fetches latest zones from your Cloudflare account
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Current Selection Indicator */}
        {selectedZone && (
          <Card sx={{ bgcolor: '#f38020', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                🎯 Currently Purging On:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedZone.name}
              </Typography>
              <Typography variant="caption">
                Zone ID: {selectedZone.id?.substring(0, 12)}...
              </Typography>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};

export default CloudflareLab;
